import { useEffect, useRef } from 'react';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiList,
  FiAlignLeft,
} from 'react-icons/fi';
import { MdFormatListNumbered } from 'react-icons/md';
import './RichTextEditor.scss';

interface Props {
  /** Set once at mount (and whenever `resetKey` changes) — this is an uncontrolled editor, not a
   * controlled `value` prop, to avoid clobbering the caret on every keystroke re-render. */
  initialValue: string;
  onChange: (html: string) => void;
  placeholder?: string;
  hasError?: boolean;
  /** Bump this when `initialValue` should be re-applied (e.g. after an edit-mode fetch resolves). */
  resetKey?: string | number;
}

/**
 * Minimal hand-rolled rich text editor (contentEditable + document.execCommand) for the "Waiver
 * Agreement" field. No WYSIWYG library exists anywhere in this codebase yet (checked package.json
 * and grepped for contentEditable/quill/tiptap/slate/draft-js — nothing), so this is a small,
 * dependency-free toolbar rather than pulling in a new package for one field. execCommand is
 * deprecated but still functional in every browser this app targets for this simple use case.
 */
export default function RichTextEditor({ initialValue, onChange, placeholder, hasError, resetKey }: Props) {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialValue || '';
    }
    // Only re-sync when resetKey changes (e.g. edit-mode data arrives) — not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    onChange(editorRef.current?.innerHTML ?? '');
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML ?? '');
  };

  return (
    <div className={`rich-text-editor ${hasError ? 'field-error' : ''}`}>
      <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
        <button type="button" onClick={() => exec('bold')} aria-label="Bold" title="Bold">
          <FiBold size={16} />
        </button>
        <button type="button" onClick={() => exec('italic')} aria-label="Italic" title="Italic">
          <FiItalic size={16} />
        </button>
        <button type="button" onClick={() => exec('underline')} aria-label="Underline" title="Underline">
          <FiUnderline size={16} />
        </button>
        <span className="rich-text-toolbar-divider" />
        <button
          type="button"
          onClick={() => exec('insertUnorderedList')}
          aria-label="Bullet list"
          title="Bullet list"
        >
          <FiList size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec('insertOrderedList')}
          aria-label="Numbered list"
          title="Numbered list"
        >
          <MdFormatListNumbered size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec('justifyLeft')}
          aria-label="Align left"
          title="Align left"
        >
          <FiAlignLeft size={16} />
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-text-content"
        contentEditable
        onInput={handleInput}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
}
